/**
 * Demo Configuration
 *
 * Configurazione centralizzata per i dati demo.
 * DEMO_TODAY è la data di riferimento usata come "oggi" in modalità demo.
 *
 * Questo permette di:
 * 1. Avere contenuti demo sempre visibili senza dover aggiornare le date
 * 2. Testare funzionalità date-sensitive (scadenze, reminder, etc.)
 * 3. Passare facilmente a date dinamiche in produzione
 */

// =============================================================================
// DEMO DATE CONFIGURATION
// =============================================================================

/**
 * Data di riferimento per i demo.
 * Tutti i contenuti "di oggi" saranno relativi a questa data.
 * In produzione, sostituire con new Date().toISOString().split('T')[0]
 */
export const DEMO_TODAY = '2026-01-07';

/**
 * Timestamp completo per DEMO_TODAY (inizio giornata)
 */
export const DEMO_TODAY_START = `${DEMO_TODAY}T00:00:00Z`;

/**
 * Timestamp completo per DEMO_TODAY (fine giornata)
 */
export const DEMO_TODAY_END = `${DEMO_TODAY}T23:59:59Z`;

/**
 * Flag per abilitare/disabilitare la modalità demo
 * In produzione impostare a false
 */
export const DEMO_MODE = false; // Disabled - backend doesn't support ProjectInput type for seeding

// =============================================================================
// DATE HELPERS
// =============================================================================

/**
 * Restituisce la data "oggi" - in demo mode usa DEMO_TODAY,
 * altrimenti usa la data reale
 */
export function getToday(): string {
  if (DEMO_MODE) {
    return DEMO_TODAY;
  }
  return new Date().toISOString().split('T')[0];
}

/**
 * Restituisce il timestamp "adesso" - in demo mode usa un orario
 * ragionevole del DEMO_TODAY, altrimenti usa l'ora reale
 */
export function getNow(): string {
  if (DEMO_MODE) {
    return `${DEMO_TODAY}T10:30:00Z`;
  }
  return new Date().toISOString();
}

/**
 * Calcola una data relativa a DEMO_TODAY
 * @param daysOffset - numero di giorni (positivo = futuro, negativo = passato)
 */
export function getDemoDate(daysOffset: number): string {
  const baseDate = new Date(DEMO_TODAY);
  baseDate.setDate(baseDate.getDate() + daysOffset);
  return baseDate.toISOString().split('T')[0];
}

/**
 * Calcola un timestamp relativo a DEMO_TODAY
 * @param daysOffset - numero di giorni
 * @param hours - ora del giorno (0-23)
 * @param minutes - minuti (0-59)
 */
export function getDemoTimestamp(daysOffset: number, hours: number = 10, minutes: number = 0): string {
  const baseDate = new Date(DEMO_TODAY);
  baseDate.setDate(baseDate.getDate() + daysOffset);
  baseDate.setHours(hours, minutes, 0, 0);
  return baseDate.toISOString();
}

/**
 * Verifica se una data è "oggi" in modalità demo
 */
export function isToday(dateString: string): boolean {
  const today = getToday();
  const dateOnly = dateString.split('T')[0];
  return dateOnly === today;
}

/**
 * Verifica se una data è nel passato rispetto a DEMO_TODAY
 */
export function isPast(dateString: string): boolean {
  const today = getToday();
  const dateOnly = dateString.split('T')[0];
  return dateOnly < today;
}

/**
 * Verifica se una data è nel futuro rispetto a DEMO_TODAY
 */
export function isFuture(dateString: string): boolean {
  const today = getToday();
  const dateOnly = dateString.split('T')[0];
  return dateOnly > today;
}

/**
 * Formatta una data relativa (Oggi, Ieri, Domani, etc.)
 */
export function formatRelativeDate(dateString: string): string {
  const today = getToday();
  const dateOnly = dateString.split('T')[0];

  const todayDate = new Date(today);
  const targetDate = new Date(dateOnly);
  const diffDays = Math.round((targetDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Oggi';
  if (diffDays === 1) return 'Domani';
  if (diffDays === -1) return 'Ieri';
  if (diffDays > 1 && diffDays <= 7) return `Tra ${diffDays} giorni`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} giorni fa`;

  return targetDate.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: targetDate.getFullYear() !== todayDate.getFullYear() ? 'numeric' : undefined,
  });
}

// =============================================================================
// DEMO RANGES
// =============================================================================

/**
 * Range "questa settimana" relativo a DEMO_TODAY
 */
export function getDemoWeekRange(): { start: string; end: string } {
  const today = new Date(DEMO_TODAY);
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0],
  };
}

/**
 * Range "questo mese" relativo a DEMO_TODAY
 */
export function getDemoMonthRange(): { start: string; end: string } {
  const today = new Date(DEMO_TODAY);
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    start: firstDay.toISOString().split('T')[0],
    end: lastDay.toISOString().split('T')[0],
  };
}

export default {
  DEMO_TODAY,
  DEMO_TODAY_START,
  DEMO_TODAY_END,
  DEMO_MODE,
  getToday,
  getNow,
  getDemoDate,
  getDemoTimestamp,
  isToday,
  isPast,
  isFuture,
  formatRelativeDate,
  getDemoWeekRange,
  getDemoMonthRange,
};
